import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.ts';
import { useFilterStore } from '../stores/filter-store.ts';
import { Icon } from './Icon.tsx';

/**
 * Filial-Auswahl nach Region (= Cockpit-Gruppe). Ein Klick wählt/entwählt alle
 * Filialen einer Region; Einzel-Filialen bleiben darunter im Filiale-Filter wählbar.
 * Bildet direkt auf den store_name-Filter ab.
 */
export function RegionPicker() {
  const { data } = useQuery({ queryKey: ['regionen'], queryFn: api.regionen });
  const filters = useFilterStore((s) => s.filters);
  const setAll = useFilterStore((s) => s.setAll);
  const regionen = data?.regionen ?? [];
  if (regionen.length === 0) return null;

  const selected = new Set(
    Array.isArray(filters.store_name) ? (filters.store_name as string[]) : [],
  );

  const setStoreNames = (arr: string[]) => {
    const { store_name: _omit, ...rest } = filters;
    setAll(arr.length ? { ...rest, store_name: arr } : rest);
  };
  const toggleRegion = (names: string[], allSel: boolean) => {
    const next = new Set(selected);
    if (allSel) names.forEach((n) => next.delete(n));
    else names.forEach((n) => next.add(n));
    setStoreNames([...next]);
  };

  return (
    <div className="px-3 py-2 border-b border-slate-200 dark:border-slate-800 shrink-0">
      <div className="flex items-center justify-between mb-1.5">
        <span className="font-mono text-2xs uppercase tracking-[0.12em] font-medium text-slate-500 dark:text-slate-400 flex items-center gap-1.5">
          <Icon name="dashboard" size={11} /> Region
        </span>
        {selected.size > 0 && (
          <button
            onClick={() => setStoreNames([])}
            className="text-2xs text-slate-400 hover:text-brand-600"
            title="Filial-Auswahl zurücksetzen"
          >
            alle Filialen
          </button>
        )}
      </div>
      <div className="space-y-0.5">
        {regionen.map((r) => {
          const names = r.stores.map((s) => s.store_name);
          const selCount = names.filter((n) => selected.has(n)).length;
          const allSel = names.length > 0 && selCount === names.length;
          const partial = selCount > 0 && !allSel;
          return (
            <button
              key={r.id}
              onClick={() => toggleRegion(names, allSel)}
              className={`w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-xs transition-colors ${
                allSel || partial
                  ? 'bg-brand-50 dark:bg-brand-950 text-brand-700 dark:text-brand-300'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
              }`}
              title={`${r.name} — ${names.length} Filialen`}
            >
              <span
                className={`w-3.5 h-3.5 rounded border flex items-center justify-center shrink-0 ${
                  allSel
                    ? 'bg-brand-600 border-brand-600 text-white'
                    : partial
                      ? 'border-brand-500'
                      : 'border-slate-300 dark:border-slate-600'
                }`}
              >
                {allSel && <Icon name="check" size={10} />}
                {partial && <span className="w-1.5 h-0.5 bg-brand-500 rounded-sm" />}
              </span>
              <span className="flex-1 text-left font-medium truncate">{r.name}</span>
              <span className="text-2xs text-slate-400 tabular-nums">
                {selCount > 0 ? `${selCount}/${names.length}` : names.length}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
